import {Component, OnInit, HostListener, ChangeDetectionStrategy} from "@angular/core";
import {NgForm} from "@angular/forms";
import {ActivatedRoute, Router} from "@angular/router";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {NzMessageService} from "ng-zorro-antd/message";
import {lastValueFrom} from "rxjs";
import {
    DocumentTypeAliasDto,
    DocumentTypeDto,
    DocumentTypeKeywordAliasDto,
    DocumentTypeKeywordDto,
    FileTypeDto,
    PerFormaAPIService
} from "src/app/services/pfapi.service";

@Component({
    selector: "app-document-type-edit",
    templateUrl: "./document-type-edit.component.html",
    styleUrl: "./document-type-edit.component.scss",
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class DocumentTypeEditComponent implements OnInit {
    constructor(
        private readonly api: PerFormaAPIService,
        private route: ActivatedRoute,
        private router: Router,
        private message: NzMessageService,
        public modal: NgbModal
    ) {}

    loading = true;
    isEditMode = false;
    today = new Date();
    ModalOptions = {animation: false, backdropClass: "backdrop"};

    // the following object is used to check
    // if any unsaved changes are present when
    // the user attempts to navigate away
    originalDocType: DocumentTypeDto | null = null;

    //#region Document Type Details

    selectedFileType = "";
    availableFileTypes: FileTypeDto[] = [];
    selectableFileTypes = [];

    //#endregion

    //#region Document Type Aliases

    docTypeAlias = "";

    docTypeAliasColumns = [
        {
            title: "Alias"
        },
        {
            title: ""
        }
    ];

    //#endregion

    //#region Keywords

    selectedKeyword = "";
    selectableKeywords: {label: string; value: string}[] = [];
    docTypeKeywords: DocumentTypeKeywordDto[] = [];

    keywordColumns = [
        {
            title: "Name"
        },
        {
            title: "Validation Regex Override"
        },
        {
            title: "Date Format String Override"
        },
        {
            title: "Required"
        },
        {
            title: "Display Order"
        },
        {
            title: "Show In Search Results"
        },
        {
            title: ""
        }
    ];

    //#endregion

    //#region KeywordAliases

    selectedAliasKeyword = "";
    keywordAlias = "";
    selectableAliasKeywords: {label: string; value: string}[] = [];

    keywordAliasColumns = [
        {
            title: "Keyword"
        },
        {
            title: "Node Name"
        },
        {
            title: ""
        }
    ];

    //#endregion

    // default document type object. replaced if this is an edit
    documentType: DocumentTypeDto = {
        DocumentTypeID: null,
        DocumentTypeName: "",
        Alias: "",
        FileType: {} as FileTypeDto,
        Aliases: [],
        Keywords: [],
        KeywordAliases: []
    };

    async ngOnInit() {
        this.route.queryParams.subscribe(p => {
            if (p.created === "true") {
                this.message.success("Created document type");
            }
        });

        await this.checkEditMode();
    }

    checkEditMode = async () => {
        const id = this.route.snapshot.queryParamMap.get("id");

        if (!id) {
            this.setSelectableFileTypes();
            this.loading = false;
            return;
        }

        this.isEditMode = true;

        try {
            this.documentType = await lastValueFrom(this.api.getApiConfigureDocumentTypesId(Number(id)));
            this.originalDocType = JSON.parse(JSON.stringify(this.documentType));
            this.doSetup();
        } catch (e) {
            const errors = e.error?.ModelState?.status;

            if (errors && Array.isArray(errors)) {
                errors.forEach(e => this.message.error(e));
            }
        } finally {
            this.loading = false;
        }
    };

    doSetup = () => {
        this.setDocumentTypeAliases();
        this.setKeywords();
        this.setKeywordAliases();
    };

    create = async (frm: NgForm) => {
        frm.control.markAllAsTouched();

        if (!frm.valid) return;

        this.loading = true;

        try {
            const filetype = this.availableFileTypes.find(x => x.FileTypeID == Number(this.selectedFileType));

            if (filetype) {
                this.documentType.FileType = this.availableFileTypes.find(
                    x => x.FileTypeID == Number(this.selectedFileType)
                );
            } else {
                this.message.error("invalid file type");
                return;
            }

            // we get the result just for the new id
            const docType = await lastValueFrom(this.api.postApiConfigureDocumentTypes(this.documentType));

            this.router.navigateByUrl(`/configure/documenttypeedit?id=${docType.DocumentTypeID}&created=true`);
        } catch (e) {
            const errors = e.error?.ModelState?.status;

            if (errors && Array.isArray(errors)) {
                errors.forEach(e => this.message.error(e));
            }
        } finally {
            this.loading = false;
        }
    };

    save = async () => {
        this.loading = true;

        try {
            const resp = await lastValueFrom(this.api.putApiConfigureDocumentTypes(this.documentType));
            this.documentType = resp;
            this.message.success("Saved changes");
            this.updateOriginalData();
            this.doSetup();
        } catch (e) {
            const errors = e.error?.ModelState?.status;

            if (errors && Array.isArray(errors)) {
                errors.forEach(e => this.message.error(e));
            }
        } finally {
            this.loading = false;
        }
    };

    RemoveDocType = template => {
        this.modal.open(template, this.ModalOptions);
    };

    remove = async () => {
        this.CloseModal();

        try {
            this.loading = true;
            await lastValueFrom(this.api.deleteApiConfigureDocumentTypesId(Number(this.documentType.DocumentTypeID)));
            this.router.navigateByUrl("/configure/documenttypes");
        } catch (e) {
            const errors = e.error?.ModelState?.status;

            if (errors && Array.isArray(errors)) {
                errors.forEach(e => this.message.error(e));
            }
        } finally {
            this.loading = false;
        }
    };

    //#region Document Type Aliases

    setDocumentTypeAliases = () => {
        this.docTypeAlias = "";
    };

    addDocTypeAlias = () => {
        const alias = this.docTypeAlias.trim();

        if (alias.length == 0) return;

        if (
            this.documentType.Aliases.find(
                x => x.DocumentTypeAliasName.toLocaleLowerCase() == alias.toLocaleLowerCase()
            )
        ) {
            this.message.error("That alias already exists");
            return;
        }

        const newAlias: DocumentTypeAliasDto = {
            DocumentTypeID: this.documentType.DocumentTypeID,
            DocumentTypeAliasName: alias,
            DocumentTypeAliasID: this.documentType.Aliases.length + 1
        };

        this.documentType.Aliases = [...this.documentType.Aliases, newAlias];
        this.setDocumentTypeAliases();
    };

    // if they are removing a document type alias row, we
    // just remove it from the array
    removeDocTypeAlias = (id: number) => {
        this.documentType.Aliases = this.documentType.Aliases.filter(t => t.DocumentTypeAliasID !== id);
        this.setDocumentTypeAliases();
    };

    //#endregion

    //#region Keywords

    addKeyword = (id: string) => {
        const item = this.documentType.Keywords.find(
            x => x.MetaDataDefinitionID === Number(id) && x.DocumentTypeMetaDataDefinitionID == null
        );

        if (item) {
            item.DocumentTypeMetaDataDefinitionID = 0;
            this.setKeywords();
        }
    };

    // if they are removing a keyword row, we
    // just null out the unique id
    // so that it will go back into the dropdown of available keywords
    removeKeyword = (id: number) => {
        const item = this.documentType.Keywords.find(
            x => x.MetaDataDefinitionID === id && x.DocumentTypeMetaDataDefinitionID != null
        );

        if (item) {
            item.DocumentTypeMetaDataDefinitionID = null;
            this.setKeywords();
        }
    };

    setSelectableKeywords = () => {
        this.selectableKeywords = this.documentType.Keywords.filter(
            x => x.DocumentTypeMetaDataDefinitionID == null
        ).map(x => {
            return {
                label: x.Name,
                value: x.MetaDataDefinitionID.toString()
            };
        });
    };

    setDocumentTypeKeywords = () => {
        this.docTypeKeywords = this.documentType.Keywords.filter(x => x.DocumentTypeMetaDataDefinitionID != null);
    };

    setKeywords = () => {
        this.selectedKeyword = "";
        this.setSelectableKeywords();
        this.setDocumentTypeKeywords();
        this.setKeywordAliases();
    };

    //#endregion

    //#region Keyword Aliases

    addKeywordAlias = () => {
        const alias = this.keywordAlias.trim();
        const keywordId = Number(this.selectedAliasKeyword);

        if (alias.length == 0) return;

        if (
            this.documentType.KeywordAliases.find(
                x => x.MetaDataDefinitionID == keywordId && x.Alias.toLocaleLowerCase() == alias.toLocaleLowerCase()
            )
        ) {
            this.message.error("That alias already exists");
            return;
        }

        const keyword = this.documentType.Keywords.find(
            x => x.DocumentTypeMetaDataDefinitionID != null && x.MetaDataDefinitionID === keywordId
        );

        if (keyword) {
            const newAlias: DocumentTypeKeywordAliasDto = {
                MetaDataDefinitionAliasID: this.documentType.KeywordAliases.length,
                MetaDataDefinitionID: keyword.MetaDataDefinitionID,
                KeywordName: keyword.Name,
                DocumentTypeID: this.documentType.DocumentTypeID,
                Alias: alias
            };

            this.documentType.KeywordAliases = [...this.documentType.KeywordAliases, newAlias];
            this.setKeywordAliases();
        }
    };

    removeKeywordAlias = (id: number) => {
        this.documentType.KeywordAliases = this.documentType.KeywordAliases.filter(
            x => x.MetaDataDefinitionAliasID != id
        );
        this.setKeywordAliases();
    };

    // the selectable keyword alias keywords are limited to
    // whatever keywords are *saved* to the document type
    setSelectableKeywordAliases = () => {
        this.selectableAliasKeywords = this.documentType.Keywords.filter(
            x => x.DocumentTypeMetaDataDefinitionID != null
        ).map(x => {
            return {
                label: x.Name,
                value: x.MetaDataDefinitionID.toString()
            };
        });
    };

    setKeywordAliases = () => {
        this.keywordAlias = "";
        this.selectedAliasKeyword = null;
        this.setSelectableKeywordAliases();
    };

    //#endregion

    //#region Helper Methods

    // check for unsaved changes
    isUnsavedChanges(): boolean {
        if (!this.originalDocType) {
            return false;
        }

        return JSON.stringify(this.documentType) !== JSON.stringify(this.originalDocType);
    }

    // listen for browser refresh/close
    @HostListener("window:beforeunload", ["$event"])
    beforeUnload(event: BeforeUnloadEvent) {
        if (this.isUnsavedChanges()) {
            event.preventDefault();

            // Legacy/fallback: Set if the property exists (future-proofing)
            if ("returnValue" in event) {
                event.returnValue = true;
            }
        }
    }

    setSelectableFileTypes = async () => {
        const resp = await lastValueFrom(this.api.getApiConfigureFileTypes());

        this.availableFileTypes = resp;

        this.selectableFileTypes = resp.map(f => {
            return {
                label: `${f.FileTypeName} (${f.FileTypeExtention})`,
                value: f.FileTypeID.toString()
            };
        });
    };

    // when the regex validation string changes, check the validity
    onRegexChange(regexValue: string, keyword: DocumentTypeKeywordDto) {
        if (regexValue.length == 0) {
            keyword.RegexIsValid = true;
        }

        try {
            new RegExp(regexValue);
            keyword.RegexIsValid = true;
        } catch {
            keyword.RegexIsValid = false;
        }
    }

    // closes the page modal
    CloseModal = () => this.modal.dismissAll();

    reset = () => {
        this.documentType = JSON.parse(JSON.stringify(this.originalDocType));
        this.doSetup();
    };

    goBack = () => {
        if (this.isUnsavedChanges()) {
            if (!confirm("You have unsaved changes. Are you sure you want to leave?")) {
                return;
            }
        }

        this.router.navigateByUrl("/configure/documenttypes");
    };

    // when various sections are saved, update the original data to mark it "clean"
    updateOriginalData = () => {
        this.originalDocType = JSON.parse(JSON.stringify(this.documentType));
    };

    //#endregion
}
